import useEventListener from '@propertypal/shared/src/hooks/useEventListener';
import React, { KeyboardEventHandler, ReactNode, useEffect, useRef } from 'react';
import { Rail, Slide, Viewport } from './ImageSlider.style';

const LOOP_MULTIPLIER = 3;
const DRAG_THRESHOLD = 45;
const CLICK_THRESHOLD = 6;
const TRANSITION_MS = 280;

type SlidesPerView = 1 | 'auto';

type RenderedSlide = {
  index: number;
  renderedIndex: number;
  isMiddleCopy: boolean;
};

type DragData = {
  active: boolean;
  isCapturing: boolean;
  pointerId: number | null;
  startX: number;
  deltaX: number;
  startTranslate: number;
};

export type ImageSliderRenderArgs<T> = {
  item: T;
  index: number;
  renderedIndex: number;
  isActive: boolean;
  isVisible: boolean;
  isMiddleCopy: boolean;
};

export interface ImageSliderProps<T> {
  items: T[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  slidesPerView?: SlidesPerView;
  disableEvents?: boolean;
  canDrag?: boolean;
  getItemKey: (item: T, index: number) => string;
  getItemAspectRatio: (item: T, index: number) => number;
  renderSlide: (args: ImageSliderRenderArgs<T>) => ReactNode;
  renderNavigation?: (controls: { goPrev: () => void; goNext: () => void }) => ReactNode;
}

const normalizeIndex = (index: number, length: number) => {
  if (length === 0) return 0;

  return ((index % length) + length) % length;
};

const getVisibleIndexes = <T,>(items: T[], index: number) => {
  if (items.length <= 5) {
    return items.map((_, itemIndex) => itemIndex);
  }

  return [
    normalizeIndex(index - 2, items.length),
    normalizeIndex(index - 1, items.length),
    index,
    normalizeIndex(index + 1, items.length),
    normalizeIndex(index + 2, items.length),
  ];
};

const getRenderedSlides = <T,>(items: T[]): RenderedSlide[] => {
  if (items.length <= 1) {
    return items.map((_, index) => ({ index, renderedIndex: index, isMiddleCopy: true }));
  }

  return Array.from({ length: items.length * LOOP_MULTIPLIER }, (_, renderedIndex) => ({
    index: renderedIndex % items.length,
    renderedIndex,
    isMiddleCopy: renderedIndex >= items.length && renderedIndex < items.length * 2,
  }));
};

const ImageSlider = <T,>(props: ImageSliderProps<T>) => {
  const lastKeyPress = useRef<number>(0);
  const mounted = useRef(true);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const animationFrameRef = useRef<number | null>(null);
  const currentVisualIndexRef = useRef<number | null>(null);
  const translateXRef = useRef(0);
  const dragRef = useRef<DragData>({
    active: false,
    pointerId: null,
    startX: 0,
    deltaX: 0,
    startTranslate: 0,
    isCapturing: false,
  });
  const suppressClickRef = useRef(false);
  const hasInitialLayoutRef = useRef(false);
  const sliderMode = props.slidesPerView === 1 ? 'single' : 'auto';
  const canLoop = props.items.length > 1;
  const canDrag = props.canDrag ?? true;
  const safeActiveIndex = normalizeIndex(props.activeIndex ?? 0, Math.max(props.items.length, 1));
  const visibleIndexes = getVisibleIndexes(props.items, safeActiveIndex);
  const renderedSlides = getRenderedSlides(props.items);

  const applyTranslate = (translateX: number, animate: boolean) => {
    if (!railRef.current) return;

    railRef.current.style.transition = animate ? `transform ${TRANSITION_MS}ms ease` : 'none';
    railRef.current.style.transform = `translate3d(${translateX}px, 0, 0)`;
    translateXRef.current = translateX;
  };

  const getSlideCenter = (renderedIndex: number) => {
    const slide = slideRefs.current[renderedIndex];

    if (!slide) return 0;

    return slide.offsetLeft + slide.clientWidth / 2;
  };

  const getTranslateForRenderedIndex = (renderedIndex: number) => {
    const viewportWidth = viewportRef.current?.clientWidth || 0;
    const slideCenter = getSlideCenter(renderedIndex);

    return viewportWidth / 2 - slideCenter;
  };

  const getRenderedCandidates = (index: number) => {
    if (!canLoop) return [index];

    return [index, index + props.items.length, index + props.items.length * 2];
  };

  const getNearestRenderedIndex = (index: number) => {
    const fallbackIndex = canLoop ? index + props.items.length : index;
    const currentVisualIndex = currentVisualIndexRef.current ?? fallbackIndex;

    return getRenderedCandidates(index).reduce((closestIndex, candidate) => {
      return Math.abs(candidate - currentVisualIndex) < Math.abs(closestIndex - currentVisualIndex)
        ? candidate
        : closestIndex;
    }, fallbackIndex);
  };

  const syncToIndex = (index: number, immediate?: boolean) => {
    if (!viewportRef.current || !renderedSlides.length) return;

    const renderedIndex = getNearestRenderedIndex(index);
    currentVisualIndexRef.current = renderedIndex;
    applyTranslate(getTranslateForRenderedIndex(renderedIndex), !immediate);
  };

  const scheduleSync = (immediate?: boolean) => {
    if (animationFrameRef.current != null) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = window.requestAnimationFrame(() => {
      syncToIndex(safeActiveIndex, immediate ?? !hasInitialLayoutRef.current);
      hasInitialLayoutRef.current = true;
    });
  };

  const requestIndexChange = (nextIndex: number) => {
    if (props.disableEvents || !mounted.current || props.items.length === 0) return;

    props.setActiveIndex(normalizeIndex(nextIndex, props.items.length));
  };

  const goNext = () => {
    requestIndexChange(safeActiveIndex + 1);
  };

  const goPrev = () => {
    requestIndexChange(safeActiveIndex - 1);
  };

  const handleKeyPress: KeyboardEventHandler = (event) => {
    const now = new Date().getTime();

    if (now > lastKeyPress.current + 500) {
      if (event.key === 'ArrowRight') {
        goNext();
      } else if (event.key === 'ArrowLeft') {
        goPrev();
      }

      lastKeyPress.current = now;
    }
  };

  const handleKeyUp = () => {
    lastKeyPress.current = 0;
  };

  useEventListener('keydown', handleKeyPress, !props.disableEvents);
  useEventListener('keyup', handleKeyUp, !props.disableEvents);
  useEventListener('resize', () => scheduleSync(true), props.items.length > 1);

  useEffect(() => {
    scheduleSync();

    return () => {
      if (animationFrameRef.current != null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [safeActiveIndex, props.items.length, props.slidesPerView]);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleTransitionEnd = () => {
    if (!canLoop || currentVisualIndexRef.current == null) return;

    if (currentVisualIndexRef.current < props.items.length || currentVisualIndexRef.current >= props.items.length * 2) {
      currentVisualIndexRef.current = safeActiveIndex + props.items.length;
      applyTranslate(getTranslateForRenderedIndex(currentVisualIndexRef.current), false);
    }
  };

  const getClosestRenderedIndex = () => {
    const viewportWidth = viewportRef.current?.clientWidth || 0;
    const visualCenter = viewportWidth / 2 - translateXRef.current;

    return renderedSlides.reduce(
      (closestSlide, slide) => {
        const distance = Math.abs(getSlideCenter(slide.renderedIndex) - visualCenter);

        if (distance < closestSlide.distance) {
          return { distance, renderedIndex: slide.renderedIndex };
        }

        return closestSlide;
      },
      { distance: Number.POSITIVE_INFINITY, renderedIndex: currentVisualIndexRef.current ?? safeActiveIndex },
    ).renderedIndex;
  };

  const completeDrag = () => {
    const drag = dragRef.current;

    if (!drag.active) return;

    drag.active = false;
    drag.pointerId = null;
    suppressClickRef.current = Math.abs(drag.deltaX) > CLICK_THRESHOLD;

    if (!canLoop || !canDrag) {
      scheduleSync();
      return;
    }

    if (sliderMode === 'single') {
      if (drag.deltaX <= -DRAG_THRESHOLD) {
        goNext();
      } else if (drag.deltaX >= DRAG_THRESHOLD) {
        goPrev();
      } else {
        scheduleSync();
      }

      return;
    }

    const closestRenderedIndex = getClosestRenderedIndex();
    const closestIndex = renderedSlides[closestRenderedIndex]?.index ?? safeActiveIndex;
    const hasDraggedFarEnough = Math.abs(drag.deltaX) >= DRAG_THRESHOLD;

    if (Math.abs(drag.deltaX) <= CLICK_THRESHOLD && closestIndex === safeActiveIndex) {
      scheduleSync();
      return;
    }

    if (closestIndex !== safeActiveIndex) {
      requestIndexChange(closestIndex);
      return;
    }

    if (hasDraggedFarEnough) {
      requestIndexChange(safeActiveIndex + (drag.deltaX < 0 ? 1 : -1));
      return;
    }

    scheduleSync();
  };

  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (!canLoop || !canDrag) return;
    if (event.pointerType !== 'touch' && event.button !== 0) return;

    dragRef.current = {
      active: true,
      isCapturing: false,
      pointerId: event.pointerId,
      startX: event.clientX,
      deltaX: 0,
      startTranslate: translateXRef.current,
    };

    applyTranslate(translateXRef.current, false);
  };

  const handlePointerMove: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId || !canDrag) return;

    const deltaX = event.clientX - dragRef.current.startX;
    dragRef.current.deltaX = deltaX;

    if (!dragRef.current.isCapturing && Math.abs(deltaX) > CLICK_THRESHOLD) {
      event.currentTarget.setPointerCapture?.(event.pointerId);
      dragRef.current.isCapturing = true;
    }

    applyTranslate(dragRef.current.startTranslate + deltaX, false);
  };

  const handlePointerUp: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (dragRef.current.pointerId !== event.pointerId) return;

    completeDrag();
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const handleClickCapture: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (!suppressClickRef.current) return;

    suppressClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  if (props.items.length <= 1) {
    return (
      <>
        {props.renderNavigation?.({ goPrev, goNext })}
        <Viewport $canDrag={false} onClickCapture={handleClickCapture}>
          {props.items.map((item, index) => (
            <Slide
              key={props.getItemKey(item, index)}
              ref={(node) => {
                slideRefs.current[index] = node;
              }}
              $mode="single"
              $ratio={props.getItemAspectRatio(item, index)}
              aria-current="true"
              data-active
              data-print-active
              data-slider-slide="true"
            >
              {props.renderSlide({
                item,
                index,
                renderedIndex: index,
                isActive: true,
                isVisible: true,
                isMiddleCopy: true,
              })}
            </Slide>
          ))}
        </Viewport>
      </>
    );
  }

  return (
    <>
      {props.renderNavigation?.({ goPrev, goNext })}
      <Viewport
        ref={viewportRef}
        $canDrag={canDrag}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={completeDrag}
        onClickCapture={handleClickCapture}
      >
        <Rail ref={railRef} data-slider-rail="true" data-testid="slider-rail" onTransitionEnd={handleTransitionEnd}>
          {renderedSlides.map((slide) => {
            const item = props.items[slide.index];
            const isActive = slide.isMiddleCopy && slide.index === safeActiveIndex;
            const isVisible = visibleIndexes.includes(slide.index);

            return (
              <Slide
                key={`${props.getItemKey(item, slide.index)}-${slide.renderedIndex}`}
                ref={(node) => {
                  slideRefs.current[slide.renderedIndex] = node;
                }}
                $mode={sliderMode}
                $ratio={props.getItemAspectRatio(item, slide.index)}
                aria-current={isActive ? 'true' : undefined}
                data-active={isActive}
                data-print-active={isActive}
                data-slider-slide="true"
              >
                {props.renderSlide({
                  item,
                  index: slide.index,
                  renderedIndex: slide.renderedIndex,
                  isActive,
                  isVisible,
                  isMiddleCopy: slide.isMiddleCopy,
                })}
              </Slide>
            );
          })}
        </Rail>
      </Viewport>
    </>
  );
};

export default ImageSlider;
